import * as React from 'react';
import moment from 'moment';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';

import { Button, Loader, PageBody, PageTitle } from 'src/components';
import { X } from 'src/common/icons';
import { TNotification, TNotificationCategory, TTaskAction } from 'src/types/TNotification';
import { useAllNotificationsQuery } from 'src/api/queries/notificationQueries';
import {
  useDeleteNotificationsMutation,
  useResolveTaskMutation,
} from 'src/api/mutations/notificationMutations';
import { config } from 'src/utils/config';
import { openNotificationUrl } from 'src/utils/notificationLink';

import styles from './styles.module.scss';

type TFilter = 'all' | TNotificationCategory;

// Display order for the filter chips. Tasks first since they are actionable.
const CATEGORY_ORDER: TNotificationCategory[] = [
  'tasks',
  'media',
  'insights',
  'billing',
  'activities',
];

const CATEGORY_LABELS: Record<TNotificationCategory, string> = {
  tasks: 'Tasks',
  media: 'Media',
  insights: 'Insights',
  billing: 'Billing',
  activities: 'Activity',
};

const CategoryPill: React.FC<{ category: TNotificationCategory }> = ({ category }) => (
  <span className={cn(styles.typePill, styles[`type_${category}`])}>
    {CATEGORY_LABELS[category]}
  </span>
);

type FilterBarProps = {
  active: TFilter;
  counts: Record<TFilter, number>;
  available: TNotificationCategory[];
  onChange: (filter: TFilter) => void;
};

const FilterBar: React.FC<FilterBarProps> = ({ active, counts, available, onChange }) => {
  const renderChip = (value: TFilter, label: string) => (
    <button
      key={value}
      type="button"
      role="tab"
      aria-selected={active === value}
      className={cn(styles.filterChip, { [styles.filterChipActive]: active === value })}
      onClick={() => onChange(value)}
      data-test={`notifications-filter-${value}`}
    >
      <span>{label}</span>
      <span className={styles.filterCount}>{counts[value]}</span>
    </button>
  );

  return (
    <div className={styles.filterBar} role="tablist" aria-label="Filter notifications">
      {renderChip('all', 'All')}
      {available.map((category) => renderChip(category, CATEGORY_LABELS[category]))}
    </div>
  );
};

const MessageBody: React.FC<{
  notification: TNotification;
  onOpen: (notification: TNotification) => void;
}> = ({ notification, onOpen }) => {
  const clickable = Boolean(notification.url);

  return (
    <div className={styles.rowMain}>
      <div className={styles.rowMeta}>
        <CategoryPill category={notification.category} />
        <span className={styles.timestamp}>{moment(notification.timestamp).fromNow()}</span>
      </div>
      <p className={styles.message}>
        {notification.message}
        {clickable && (
          <>
            {' \u2014 '}
            <a
              className={styles.openLink}
              href={notification.url}
              onClick={(event) => {
                event.preventDefault();
                onOpen(notification);
              }}
            >
              Click to open
            </a>
          </>
        )}
      </p>
      {notification.assigned_by_email && (
        <span className={styles.assigner}>Assigned by {notification.assigned_by_email}</span>
      )}
    </div>
  );
};

type NotificationRowProps = {
  notification: TNotification;
  onOpen: (notification: TNotification) => void;
  onDismiss: (notification: TNotification) => void;
};

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onOpen, onDismiss }) => (
  <li className={styles.row} data-test="notification-item">
    <MessageBody notification={notification} onOpen={onOpen} />
    <div className={styles.actions}>
      <Button
        className={styles.pillBtn}
        size="small"
        mode="tertiary"
        icon={<X />}
        onClick={() => onDismiss(notification)}
      >
        Dismiss
      </Button>
    </div>
  </li>
);

type TaskRowProps = {
  notification: TNotification;
  onOpen: (notification: TNotification) => void;
  onResolve: (notification: TNotification, action: TTaskAction) => void;
  busy: boolean;
};

const TaskRow: React.FC<TaskRowProps> = ({ notification, onOpen, onResolve, busy }) => (
  <li className={styles.row} data-test="task-item">
    <MessageBody notification={notification} onOpen={onOpen} />
    <div className={styles.actions}>
      <Button
        className={styles.pillBtn}
        size="small"
        mode="accept"
        disabled={busy}
        onClick={() => onResolve(notification, 'complete')}
      >
        Complete
      </Button>
      <Button
        className={styles.pillBtn}
        size="small"
        mode="decline"
        disabled={busy}
        onClick={() => onResolve(notification, 'decline')}
      >
        Decline
      </Button>
      <Button
        className={styles.pillBtn}
        size="small"
        mode="tertiary"
        disabled={busy}
        onClick={() => onResolve(notification, 'dismiss')}
      >
        Dismiss
      </Button>
    </div>
  </li>
);

export const Notifications: React.FC = () => {
  const history = useHistory();
  const deleteNotificationsMutation = useDeleteNotificationsMutation();
  const resolveTaskMutation = useResolveTaskMutation();
  const [filter, setFilter] = React.useState<TFilter>('all');

  const { isLoading, data } = useAllNotificationsQuery(undefined, {
    refetchIntervalInBackground: true,
    refetchInterval: config.notificationsRefetchInterval,
  });

  const notifications: TNotification[] = data?.notifications ?? [];

  // Tasks only surface while OPEN; info notifications always show.
  const visible = React.useMemo(
    () => notifications.filter((n) => (n.category === 'tasks' ? n.status === 'OPEN' : true)),
    [notifications],
  );

  const counts = React.useMemo(() => {
    const base: Record<TFilter, number> = {
      all: visible.length,
      tasks: 0,
      media: 0,
      insights: 0,
      billing: 0,
      activities: 0,
    };
    visible.forEach((n) => {
      base[n.category] += 1;
    });
    return base;
  }, [visible]);

  const availableCategories = CATEGORY_ORDER.filter((category) => counts[category] > 0);

  // If the active category empties out (e.g. last item dismissed), fall back to All.
  React.useEffect(() => {
    if (filter !== 'all' && counts[filter] === 0) setFilter('all');
  }, [filter, counts]);

  const filtered = filter === 'all' ? visible : visible.filter((n) => n.category === filter);

  const handleOpen = (notification: TNotification) => openNotificationUrl(history, notification.url);

  const handleDismiss = (notification: TNotification) => {
    deleteNotificationsMutation.mutateAsync([notification.id]);
  };

  const handleResolve = (notification: TNotification, action: TTaskAction) => {
    resolveTaskMutation.mutateAsync({ id: notification.id, action });
  };

  return (
    <div className={styles.page} data-test="notifications-page-content">
      <PageTitle title="Notifications" className={styles.pageTitle} />
      <PageBody narrow>
        {isLoading && (
          <div className={styles.loading}>
            <Loader />
            <p>Loading notifications...</p>
          </div>
        )}

        {!isLoading && visible.length > 0 && (
          <FilterBar
            active={filter}
            counts={counts}
            available={availableCategories}
            onChange={setFilter}
          />
        )}

        {!isLoading && visible.length === 0 && (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>You're all caught up</p>
            <p className={styles.emptySubtitle}>New notifications will show up here.</p>
          </div>
        )}

        {!isLoading && visible.length > 0 && filtered.length === 0 && (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>Nothing here</p>
            <p className={styles.emptySubtitle}>No notifications match this filter.</p>
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <ul className={styles.list}>
            {filtered.map((notification) =>
              notification.category === 'tasks' ? (
                <TaskRow
                  key={notification.id}
                  notification={notification}
                  onOpen={handleOpen}
                  onResolve={handleResolve}
                  busy={resolveTaskMutation.isLoading}
                />
              ) : (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onOpen={handleOpen}
                  onDismiss={handleDismiss}
                />
              ),
            )}
          </ul>
        )}
      </PageBody>
    </div>
  );
};

export default Notifications;
